import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Star, Zap } from 'lucide-react';
import { useSubscription } from '@/hooks/useSubscription';

interface Plan {
  id: 'basic' | 'premium' | 'enterprise';
  name: string;
  price: string;
  description: string;
  features: string[];
  icon: React.ReactNode;
  popular?: boolean;
}

const plans: Plan[] = [
  {
    id: 'basic',
    name: 'Basic',
    price: '$9.99',
    description: 'Perfect for getting started with AI bots',
    icon: <Check className="h-5 w-5" />,
    features: [
      'Access to 50+ AI bots',
      'Basic analytics',
      'Email support',
      'Standard features'
    ]
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$19.99',
    description: 'Best for power users and professionals',
    icon: <Star className="h-5 w-5" />,
    popular: true,
    features: [
      'Access to 200+ AI bots',
      'Advanced analytics',
      'Priority support',
      'Premium features',
      'AI recommendations',
      'Custom integrations'
    ]
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: '$49.99',
    description: 'For teams and organizations',
    icon: <Zap className="h-5 w-5" />,
    features: [
      'Unlimited AI bots',
      'Team management',
      'White-label options',
      'Custom development',
      'Dedicated support',
      'SLA guarantee'
    ]
  }
];

interface SubscriptionCardProps {
  plan: Plan;
  isCurrentPlan?: boolean;
}

export const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ 
  plan, 
  isCurrentPlan = false 
}) => {
  const { loading, createCheckout } = useSubscription();

  const handleSubscribe = () => {
    createCheckout(plan.id);
  };

  return (
    <Card className={`relative h-full ${plan.popular ? 'border-primary shadow-lg' : ''}`}>
      {plan.popular && (
        <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary">
          Most Popular
        </Badge>
      )}
      
      <CardHeader className="text-center pb-4">
        <div className="flex justify-center mb-2">
          <div className={`p-3 rounded-full ${
            isCurrentPlan 
              ? 'bg-primary text-primary-foreground' 
              : 'bg-primary/10 text-primary'
          }`}>
            {plan.icon}
          </div>
        </div>
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        <div className="text-3xl font-bold">
          {plan.price}
          <span className="text-sm font-normal text-muted-foreground">/month</span>
        </div>
        <CardDescription>{plan.description}</CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <ul className="space-y-3">
          {plan.features.map((feature, index) => (
            <li key={index} className="flex items-center gap-3">
              <Check className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="text-sm">{feature}</span>
            </li>
          ))}
        </ul>
        
        <Button
          onClick={handleSubscribe}
          disabled={loading || isCurrentPlan}
          className={`w-full ${
            isCurrentPlan 
              ? 'bg-primary/20 text-primary cursor-not-allowed' 
              : plan.popular 
                ? 'bg-primary hover:bg-primary/90' 
                : ''
          }`}
          variant={plan.popular && !isCurrentPlan ? 'default' : 'outline'}
        >
          {isCurrentPlan ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Current Plan
            </>
          ) : loading ? (
            'Loading...'
          ) : (
            `Subscribe to ${plan.name}`
          )}
        </Button>
      </CardContent>
    </Card>
  );
};